/**
 * Builds a capped exponential delay sequence. Each call returns the current delay and then grows
 * it by `factor`, stopping at `max`; `reset()` returns to the starting delay.
 *
 * `max` is a hard ceiling: an `initial` above it is clamped down, so no delay this returns - the
 * first one, or any after a `reset()` - ever exceeds `max`. Callers derive `initial` from
 * configurable values (a caller-supplied polling interval, say) that can legitimately outgrow a
 * fixed ceiling, and a delay over the cap is never what they asked for.
 *
 * Useful anywhere repeated work should ease off instead of running at a fixed rate - spacing out
 * retries after a failed request, reconnect attempts, or a long-lived poll that would otherwise
 * hit an endpoint at the same cadence indefinitely. Call it for each successive delay, and
 * `reset()` once the underlying operation succeeds.
 *
 * Pairs directly with Poll's `interval` option, which accepts a function and re-reads it per cycle.
 *
 * @param {{ initial: number, max: number, factor?: number }} options
 * @returns {(() => number) & { reset: () => void, peek: () => number }}
 */
export const exponentialBackoff = ({ initial, max, factor = 2 }) => {
    const start = Math.min(initial, max);
    let current = start;
    const next = () => {
        const delay = current;
        current = Math.min(Math.round(current * factor), max);
        return delay;
    };
    next.reset = () => {
        current = start;
    };
    next.peek = () => current;
    return next;
};

export default exponentialBackoff;
